<template>
  <div v-if="findings?.length">
    <div class="full-details-wrapper">
      <template v-if="showSubtitle">
        <div class="d-flex align-center px-4 pt-4">
          <div class="subtitle1 mb-4" data-testid="tickets-page-findings-title">
            {{ $t("ticketDetails.findings") }}
          </div>
        </div>
      </template>
      <v-expansion-panels flat accordion multiple class="expansion-panel-wrapper">
        <v-expansion-panel
          v-for="(triggerFindings, eventTrigger) in groupedFindings"
          :key="eventTrigger"
          @click="handlePanelClick()"
        >
          <v-expansion-panel-title class="expansion-panel-header pl-4 pt-2 pb-2">
            <template v-slot:actions />
            <template v-slot:default="{ expanded }">
              <div class="d-flex align-center justify-space-between w-100">
                <div class="d-flex align-center">
                  <v-icon :class="{ rotated: !expanded }" icon="$chevronUp"></v-icon>
                  <div class="ml-1 subtitle2">
                    {{ $t(`tickets.eventSubTriggers.${eventTrigger}`) }}
                  </div>
                </div>
                <div class="body2 ml-1">
                  {{ triggerFindings.length }} {{ $t("ticketDetails.findings") }}
                </div>
              </div>
            </template>
          </v-expansion-panel-title>
          <v-expansion-panel-text>
            <div class="findings-wrapper">
              <div v-if="findingsDetails?.filePath" class="findings-grid mb-12">
                <div class="findings-grid__label">{{ $t("ticketDetails.filePath") }}:</div>
                <div class="findings-grid__value">{{ findingsDetails.filePath }}</div>
                <template v-if="findingsDetails.fileSize">
                  <div class="findings-grid__label">{{ $t("ticketDetails.fileSize") }}:</div>
                  <div class="findings-grid__value">
                    {{ convertSize(findingsDetails.fileSize, 1) }}
                  </div>
                </template>
              </div>
              <div class="findings-grid">
                <template
                  v-for="(finding, index) of getFindingTemplatesForTrigger(triggerFindings)"
                  :key="index"
                >
                  <div
                    class="findings-grid__row"
                    :data-testid="`tickets-page-finding-row-${index}`"
                    v-html="finding"
                  ></div>
                  <div
                    v-if="index !== getFindingTemplatesForTrigger(triggerFindings).length - 1"
                    class="findings-grid__divider"
                  />
                </template>
              </div>
            </div>
          </v-expansion-panel-text>
        </v-expansion-panel>
      </v-expansion-panels>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, type PropType } from "vue";
import type { Finding, TicketDetails } from "@/_store/tickets/tickets.module";
import { convertSize } from "@coronet/cui";
import { trackGtmEvent } from "@/_helpers/analytics";
import { createFindingTemplate } from "@/_helpers/ticket-helpers";
import type { FileFinding } from "@/_store/tickets/ticket.module";
import { GtmAction, GTMParams } from "@/constants/gtm.ts";
import type { TicketTrigger } from "@/constants/tickets.ts";

export default defineComponent({
  props: {
    ticket: {
      type: Object as PropType<TicketDetails>,
      required: false,
    },
    showTitle: {
      type: Boolean,
      default: true,
    },
    showSubtitle: {
      type: Boolean,
      default: true,
    },
    findingsDetails: {
      type: Object as PropType<FileFinding>,
      required: false,
    },
  },
  setup(props) {
    const findings = computed(
      () => props.ticket?.sections?.findings || props.findingsDetails?.findings || []
    );

    // Group findings by eventTrigger
    const groupedFindings = computed(() => {
      const grouped: Record<string, Finding[]> = {};
      findings.value.forEach((finding: Finding) => {
        const trigger =
          (finding as Finding & { eventTrigger?: TicketTrigger }).eventTrigger || "unknown";
        if (!grouped[trigger]) {
          grouped[trigger] = [];
        }
        grouped[trigger].push(finding);
      });
      return grouped;
    });

    const getSortedFindingTemplates = (findings: Finding[]) =>
      findings
        .slice()
        .sort((a, b) => {
          // if source exists - sort by source
          if (a.source) {
            return a.source.localeCompare(b.source!);
          }
          // if it's archive - sort by filePath
          if (a.filePath?.includes("#")) {
            return a.filePath.localeCompare(b.filePath);
          }
          if (a.typeUiView) {
            return a.typeUiView.localeCompare(b.typeUiView!);
          }
          return a.type.localeCompare(b.type);
        })
        .map((finding) => createFindingTemplate(finding, props.ticket!));

    const getFindingTemplatesForTrigger = (triggerFindings: Finding[]) => {
      return getSortedFindingTemplates(triggerFindings);
    };

    const handlePanelClick = () => {
      trackGtmEvent(GtmAction.TICKETS, `${GTMParams.EMAIL_CLOUD_FINDINGS_CLICKED}`);
    };

    return {
      findings,
      convertSize,
      groupedFindings,
      getFindingTemplatesForTrigger,
      handlePanelClick,
    };
  },
});
</script>

<style lang="scss" scoped>
.full-details-wrapper {
  border: 1px solid rgb(var(--v-theme-indigo-faint));
}

.expansion-panel-header {
  height: 56px;
  background-color: rgb(var(--v-theme-indigo-faint));
  border-radius: 0 !important;
}

.v-expansion-panel:not(.v-expansion-panel--active):not(:last-child) {
  border-bottom: 1px solid rgb(var(--v-theme-indigo-pale));
  border-radius: 0 !important;
}

:deep(*) {
  .v-expansion-panel-text__wrapper {
    padding: 0 16px 16px !important;
  }
  .v-expansion-panel--active:not(:first-child),
  .v-expansion-panel--active + .v-expansion-panel {
    margin-top: 0 !important;
  }
}
</style>
